import { Button } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useEffect, useId, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { ClientDetail } from '@/types';
import { TEST_IDS } from '@/testIds';
import {
  AppModal,
  FieldError,
  ModalContent,
  ModalFooter,
  ModalTopBar,
  SectionTextField,
} from '@/shared/ui';
import styles from './ClientEditModal.module.css';

interface ClientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (clientData: { name: string }) => void | Promise<void>;
  client?: ClientDetail;
  isLoading?: boolean;
}

type ClientFormValues = {
  name: string;
};

const defaultValues: ClientFormValues = {
  name: '',
};

export const ClientEditModal: FC<ClientEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  client,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ClientFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const savingRef = useRef(false);
  const nameErrorId = useId();
  const isBusy = isSubmitting || isLoading;

  useEffect(() => {
    if (isOpen) reset(client ? { name: client.name } : defaultValues);
  }, [client, isOpen, reset]);

  const handleSave = async (values: ClientFormValues): Promise<void> => {
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    clearErrors('root');
    try {
      await onSave({ name: values.name.trim() });
    } catch {
      setError('root', { message: 'Could not save the name. Your changes are kept. Try again.' });
    } finally {
      savingRef.current = false;
    }
  };

  const handleClose = (): void => {
    if (isBusy || savingRef.current) return;
    onClose();
  };

  const submitForm = handleSubmit(handleSave);

  return (
    <AppModal
      open={isOpen}
      dismissible={!isBusy}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <form
        noValidate
        aria-label="Edit client"
        onSubmit={(event) => {
          event.preventDefault();
          if (!isBusy && !savingRef.current) void submitForm();
        }}
      >
        <ModalContent>
          <ModalTopBar title="Edit client" onCancel={handleClose} cancelDisabled={isBusy} />
          <div className={styles.body}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Client name is required',
                validate: (value) => (value.trim().length > 0 ? true : 'Client name is required'),
              }}
              render={({ field }) => (
                <>
                  <SectionTextField
                    {...field}
                    label="Name"
                    ariaLabel="Client name"
                    autoComplete="name"
                    ariaDescribedBy={errors.name ? nameErrorId : undefined}
                    value={field.value}
                    onChange={field.onChange}
                    testId={TEST_IDS.clientNameInput}
                    invalid={Boolean(errors.name)}
                    disabled={isBusy}
                  />
                  <FieldError id={nameErrorId} message={errors.name?.message?.toString()} />
                </>
              )}
            />
            <FieldError message={errors.root?.message} />
          </div>
          <ModalFooter>
            <Button
              variant="primary"
              type="submit"
              fullWidth
              disabled={isSubmitting || isLoading}
              loading={isBusy}
              data-testid={TEST_IDS.updateClientNameButton}
            >
              {isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </AppModal>
  );
};
